import React, { useMemo, useState, useEffect } from "react";
import { Box, Collapse, Skeleton, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import useSchedulingStore from "./useSchedulingStore";
import ScheduledMessageDialog, {
  ScheduleStatusBadge,
} from "./ScheduledMessageDialog";
import RecurringScheduleDialog, {
  RecurringScheduleStatusBadge,
  formatRecurrenceDescription,
} from "./RecurringScheduleDialog";
import {
  formatScheduleTime,
  groupScheduledItemsBySession,
  scheduledItemNextAt,
  truncateSessionTitle,
} from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";
import { buildAgentChatUrl } from "./boards/commentUtils";

function scheduledGroupKey(group) {
  return group?.chatSessionId || "__ungrouped__";
}

function buildDefaultExpandedGroupKeys(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return new Set();
  if (groups.length <= 2) {
    return new Set(groups.map(scheduledGroupKey));
  }
  return new Set([scheduledGroupKey(groups[0])]);
}

/** A schedule item is recurring when it carries a recurrenceType; otherwise it's a one-shot. */
function isRecurringItem(item) {
  return Boolean(item?.recurrenceType);
}

/** Shared presentation for one schedule row; variant-specific bits come from the caller. */
function ScheduleRow({ icon, title, badge, meta, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        pl: 3,
        pr: 2.5,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        cursor: "pointer",
        "&:hover": { backgroundColor: "action.hover" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {icon}
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: "text.primary",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {badge}
        {meta}
      </Box>
    </Box>
  );
}

function ScheduledMessageRow({ item, onClick }) {
  const _sp = useStatusPalette();
  return (
    <ScheduleRow
      onClick={onClick}
      icon={<ScheduleIcon sx={{ fontSize: 14, color: _sp.purple.color }} />}
      title={truncateSessionTitle(item.message, "Scheduled message")}
      badge={<ScheduleStatusBadge status={item.status} />}
      meta={
        <>
          {item.resumeAt && item.status === "SCHEDULED" && (
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              {formatScheduleTime(item.resumeAt)}
            </Typography>
          )}
          {item.reason && (
            <>
              <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>·</Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "text.secondary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 120,
                }}
              >
                {item.reason}
              </Typography>
            </>
          )}
        </>
      }
    />
  );
}

function RecurringScheduleRow({ item, onClick }) {
  const _sp = useStatusPalette();
  const showNext =
    item.nextResumeAt && (item.status === "ACTIVE" || item.status === "FIRING");
  const showCount = item.occurrenceCount > 0 || item.maxOccurrences != null;
  return (
    <ScheduleRow
      onClick={onClick}
      icon={<EventRepeatOutlinedIcon sx={{ fontSize: 14, color: _sp.teal.color }} />}
      title={truncateSessionTitle(item.message, "Recurring schedule")}
      badge={<RecurringScheduleStatusBadge status={item.status} />}
      meta={
        <>
          <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
            {formatRecurrenceDescription(item)}
          </Typography>
          {showNext && (
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              · Next: {formatScheduleTime(item.nextResumeAt)}
            </Typography>
          )}
          {showCount && (
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              · {item.occurrenceCount}
              {item.maxOccurrences != null ? `/${item.maxOccurrences}` : "x"}
            </Typography>
          )}
        </>
      }
    />
  );
}

function ScheduledSessionGroup({
  group,
  agentId,
  isLast,
  expanded,
  onToggleExpanded,
  onOpenMessage,
  onOpenRecurring,
}) {
  const _sp = useStatusPalette();
  const navigate = useNavigate();
  const canNavigate = Boolean(agentId && group.chatSessionId);
  const nextAt = scheduledItemNextAt(group.items[0]);
  const nextLabel = nextAt ? formatScheduleTime(nextAt) : null;

  const handleOpenSession = (event) => {
    event.stopPropagation();
    if (!canNavigate) return;
    navigate(buildAgentChatUrl(agentId, group.chatSessionId));
  };

  return (
    <Box
      sx={{
        borderBottom: isLast ? 0 : 1,
        borderColor: "divider",
      }}
    >
      <Box
        onClick={onToggleExpanded}
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          backgroundColor: "background.subtle",
          borderBottom: expanded ? 1 : 0,
          borderColor: "divider",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: 16,
            color: "text.disabled",
            flexShrink: 0,
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {group.title}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "text.disabled", mt: 0.25 }}>
            {group.items.length} schedule{group.items.length === 1 ? "" : "s"}
            {nextLabel ? ` · Next ${nextLabel}` : ""}
          </Typography>
        </Box>
        {canNavigate && (
          <Box
            component="button"
            type="button"
            onClick={handleOpenSession}
            aria-label="Open conversation"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              p: 0,
              border: 0,
              borderRadius: "5px",
              backgroundColor: "transparent",
              color: _sp.blue.color,
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": { backgroundColor: "action.selected" },
            }}
          >
            <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
        )}
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box>
          {group.items.map((item, index) => {
            const recurring = isRecurringItem(item);
            const isLastItem = index === group.items.length - 1;
            const rowProps = {
              item,
              onClick: () =>
                recurring ? onOpenRecurring(item) : onOpenMessage(item),
            };
            return (
              <Box
                key={`${recurring ? "recurring" : "message"}-${item.scheduleId}`}
                sx={{ "& > *": { borderBottom: isLastItem ? 0 : 1 } }}
              >
                {recurring ? (
                  <RecurringScheduleRow {...rowProps} />
                ) : (
                  <ScheduledMessageRow {...rowProps} />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ScheduledTab({ agentId, sessions = [] }) {
  const [dialogItem, setDialogItem] = useState(null);
  const [recurringDialogItem, setRecurringDialogItem] = useState(null);
  const scheduledMessages = useSchedulingStore((state) => state.scheduledMessages);
  const scheduledMessagesLoading = useSchedulingStore(
    (state) => state.scheduledMessagesLoading
  );
  const recurringSchedules = useSchedulingStore((state) => state.recurringSchedules);
  const recurringSchedulesLoading = useSchedulingStore(
    (state) => state.recurringSchedulesLoading
  );

  // Fetch once on open for instant freshness. The recurring 15s poll lives in the
  // parent (ExpandedSidebar), which writes the same useSchedulingStore this tab
  // reads — a second interval here would just double the requests.
  useEffect(() => {
    if (agentId) {
      useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
      useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
    }
  }, [agentId]);

  const handleCancel = async (scheduleId) => {
    await useSchedulingStore.getState().cancelScheduledMessage(scheduleId);
    useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
  };

  const handleEdit = async (scheduleId, payload) => {
    await useSchedulingStore.getState().editScheduledMessage(scheduleId, payload);
    useSchedulingStore.getState().fetchScheduledMessagesByAgent(agentId);
  };

  const handleRecurringCancel = async (scheduleId) => {
    await useSchedulingStore.getState().cancelRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringEdit = async (scheduleId, payload) => {
    await useSchedulingStore.getState().editRecurringSchedule(scheduleId, payload);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringPause = async (scheduleId) => {
    await useSchedulingStore.getState().pauseRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const handleRecurringResume = async (scheduleId) => {
    await useSchedulingStore.getState().resumeRecurringSchedule(scheduleId);
    useSchedulingStore.getState().fetchRecurringSchedulesByAgent(agentId);
  };

  const activeMessages = useMemo(
    () =>
      scheduledMessages.filter(
        (m) => m.status === "SCHEDULED" || m.status === "RESUMING"
      ),
    [scheduledMessages]
  );
  const activeRecurring = useMemo(
    () =>
      recurringSchedules.filter((r) =>
        ["ACTIVE", "FIRING", "PAUSED", "COMPLETED"].includes(r.status)
      ),
    [recurringSchedules]
  );

  const sessionGroups = useMemo(
    () => groupScheduledItemsBySession([...activeRecurring, ...activeMessages], sessions),
    [activeMessages, activeRecurring, sessions]
  );

  // Collapse state is derived: a group follows its default expansion unless the user has
  // explicitly toggled it. Overrides are keyed by session, so they survive the 15s poll and
  // stale entries for removed groups are simply never read. No effect needed.
  const defaultExpandedKeys = useMemo(
    () => buildDefaultExpandedGroupKeys(sessionGroups),
    [sessionGroups]
  );
  const [expandOverrides, setExpandOverrides] = useState(() => new Map());

  const isGroupExpanded = (groupKey) =>
    expandOverrides.has(groupKey)
      ? expandOverrides.get(groupKey)
      : defaultExpandedKeys.has(groupKey);

  const toggleGroupExpanded = (groupKey) => {
    setExpandOverrides((prev) => {
      const next = new Map(prev);
      next.set(groupKey, !isGroupExpanded(groupKey));
      return next;
    });
  };

  const isLoading =
    (scheduledMessagesLoading && scheduledMessages.length === 0) ||
    (recurringSchedulesLoading && recurringSchedules.length === 0);

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="40%" height={14} />
          </Box>
        ))}
      </Box>
    );
  }

  if (sessionGroups.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <ScheduleIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
        <Typography
          sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.6 }}
        >
          No scheduled or recurring messages.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        {sessionGroups.map((group, index) => {
          const groupKey = scheduledGroupKey(group);
          return (
            <ScheduledSessionGroup
              key={groupKey}
              group={group}
              agentId={agentId}
              isLast={index === sessionGroups.length - 1}
              expanded={isGroupExpanded(groupKey)}
              onToggleExpanded={() => toggleGroupExpanded(groupKey)}
              onOpenMessage={setDialogItem}
              onOpenRecurring={setRecurringDialogItem}
            />
          );
        })}
      </Box>
      <ScheduledMessageDialog
        item={dialogItem}
        open={!!dialogItem}
        onClose={() => setDialogItem(null)}
        onEdit={handleEdit}
        onCancel={handleCancel}
      />
      <RecurringScheduleDialog
        item={recurringDialogItem}
        open={!!recurringDialogItem}
        onClose={() => setRecurringDialogItem(null)}
        onEdit={handleRecurringEdit}
        onCancel={handleRecurringCancel}
        onPause={handleRecurringPause}
        onResume={handleRecurringResume}
      />
    </>
  );
}
